import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';

import PageContainer from '../../../components/layout/PageContainer';
import {
  FBoxContainer, FHAntdDateTimePicker, FHButton, FHChip, FHInputField, FHPageLink, FHSelect,
  FProgressBar,
} from '../../../components/base';
import {
  ClaimRejectedStatus, ClaimRejectionReasonV2, ClaimStatus, ClaimStatusColor, ClaimStatusFontColor,
} from '../../../components/layout/billings/claim-info/constants';
import { ClaimPaymentStatus } from '../../../components/layout/billings/claim-info/enum';
import { disabledFutureDate } from '../../../helpers';
import { FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import { useChangeClaimStatus } from './hooks';
import { CHANGE_CLAIM_STATUS_PASSWORD } from './constants';
import { getChangeClaimStatusPasswordInfoText, toSortedSelectOptions } from './utils';

import type { Moment } from 'moment';
import type { SelectValueType } from '../../../utils/types';

function ChangeClaimStatusForm() {
  const { claim, loading, clearCurrent, fetchClaim, updateStatus } = useChangeClaimStatus();

  const [password, setPassword] = useState<string>('');
  const [claimId, setClaimId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [reasonForRejection, setReasonForRejection] = useState<string>('');
  const [dateRejected, setDateRejected] = useState<string>('');

  // Clear the shared claim on mount so a stale claim does not show.
  useEffect(() => {
    clearCurrent();
    return clearCurrent;
  }, [clearCurrent]);

  // Seed the dropdowns from the fetched claim, and clear the reason so a
  // second change to the same claim does not resend the old reason. The
  // rejection fields prefill from the claim so an admin editing a claim that
  // already carries them does not have to retype.
  useEffect(() => {
    if (!claim) return;
    setStatus(claim.status ?? '');
    setPaymentStatus(claim.payment_status ?? '');
    setReason('');
    setReasonForRejection(claim.reason_for_rejection ?? '');
    setDateRejected(claim.date_rejected ?? '');
  }, [claim]);

  const isPasswordVerified = password === CHANGE_CLAIM_STATUS_PASSWORD;
  // The loaded claim must match the ID in the field. Otherwise the admin
  // could edit the ID after a fetch and submit against the wrong claim.
  const isSameClaim = Boolean(claim) && claim?.id === Number(claimId.trim());
  const isStatusChanged = isSameClaim && status !== (claim?.status ?? '');
  const isPaymentStatusChanged = isSameClaim && paymentStatus !== (claim?.payment_status ?? '');
  // A rejected-type status must carry a rejection reason and a rejection date.
  const isRejectedStatus = Object.values(ClaimRejectedStatus).some((value) => value === status);
  const isSubmitDisabled =
    loading ||
    !isSameClaim ||
    (!isStatusChanged && !isPaymentStatusChanged) ||
    (isRejectedStatus && (!reasonForRejection || !dateRejected));

  // Clears the loaded claim when the ID changes, so Submit cannot act on a
  // claim that no longer matches the field. The admin must fetch again.
  const handleClaimIdChange = (value: string) => {
    setClaimId(value);
    if (claim) {
      clearCurrent();
      setStatus('');
      setPaymentStatus('');
      setReason('');
      setReasonForRejection('');
      setDateRejected('');
    }
  };

  const handleGetClaim = () => {
    const trimmed = claimId.trim();
    if (!trimmed || isNaN(Number(trimmed))) {
      toast.error('The claim ID must be a number', { toastId: 'change-claim-status-id-error' });
      return;
    }
    fetchClaim(trimmed);
  };

  const handleSubmit = () => {
    // IClaim.id is optional in the type, so narrow it before the call.
    if (!claim || claim.id === undefined) return;
    updateStatus(claim.id, {
      ...(isStatusChanged && { status }),
      ...(isPaymentStatusChanged && { payment_status: paymentStatus }),
      ...(reason.trim() && { reason: reason.trim() }),
      // Send the rejection fields only when the status itself is changing. A
      // move to a rejected-type status carries both values; a move to any
      // other status clears them with nulls. A payment-status-only change
      // leaves them untouched.
      ...(isStatusChanged &&
        (isRejectedStatus
          ? { reason_for_rejection: reasonForRejection, date_rejected: dateRejected }
          : { reason_for_rejection: null, date_rejected: null })),
    });
  };

  return (
    <PageContainer>
      {loading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer dataTestId="changeClaimStatusForm" sx={{ borderRadius: 2, width: '100%', boxShadow: 'none' }}>
          <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography textAlign="left" variant="h4">Change Claim Status</Typography>
          </Box>
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }} />
          <Typography textAlign="left" sx={{ mb: 2 }}>
            This page bypasses the usual claim guardrails. Use it with care.
          </Typography>

          {/* The admin must enter the password first. The page shows nothing else
              until the password is correct. */}
          <Grid container p={0.5} spacing={1} alignItems="center">
            <Grid item xs={2.5}><Typography textAlign="left">Password:</Typography></Grid>
            <Grid item xs={8}>
              <FHInputField
                inputProps={{ 'data-testid': 'changeClaimStatusPasswordInput' }}
                type="password"
                autoComplete="off"
                inputHeight={40}
                sx={{ boxShadow: 'none' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!password && !isPasswordVerified}
                infoText={getChangeClaimStatusPasswordInfoText(password)}
              />
            </Grid>
          </Grid>

          {isPasswordVerified && (
            <Box sx={{ mt: 2 }}>
              <Grid container p={0.5} spacing={1} alignItems="center">
                <Grid item xs={2.5}><Typography textAlign="left">Claim ID:</Typography></Grid>
                <Grid item xs={8}>
                  <FHInputField
                    inputProps={{ 'data-testid': 'claimIdInput' }}
                    inputHeight={40}
                    sx={{ boxShadow: 'none' }}
                    value={claimId}
                    onChange={(e) => handleClaimIdChange(e.target.value)}
                  />
                </Grid>
              </Grid>
              <FHButton
                data-testid="getClaimButton"
                variant="contained"
                sx={{ mt: 2, width: '200px' }}
                onClick={handleGetClaim}
                disabled={!claimId || loading}
              >
                Get claim
              </FHButton>

              {claim && !loading && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <FHChip
                      content={claim.status || 'No status'}
                      bgColor={claim.status ? ClaimStatusColor[claim.status] : FORTA_WHITE}
                      color={claim.status ? ClaimStatusFontColor[claim.status] : FORTA_BLUE.primary}
                    />
                    <FHChip content={claim.payment_status || 'Not paid'} bgColor={FORTA_WHITE} color={FORTA_BLUE.primary} />
                  </Box>

                  <Grid container p={0.5} spacing={1} alignItems="center">
                    <Grid item xs={2.5}><Typography textAlign="left">New claim status</Typography></Grid>
                    <Grid item xs={8}>
                      <FHSelect
                        data-testid="newClaimStatusDropdown"
                        fullWidth
                        inputHeight={40}
                        sx={{ boxShadow: 'none', borderRadius: '8px' }}
                        options={toSortedSelectOptions(Object.values(ClaimStatus))}
                        value={status}
                        onUpdate={(value: SelectValueType) => setStatus(String(value))}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} alignItems="center">
                    <Grid item xs={2.5}><Typography textAlign="left">New payment status</Typography></Grid>
                    <Grid item xs={8}>
                      <FHSelect
                        data-testid="newPaymentStatusDropdown"
                        fullWidth
                        inputHeight={40}
                        sx={{ boxShadow: 'none', borderRadius: '8px' }}
                        options={toSortedSelectOptions(Object.values(ClaimPaymentStatus))}
                        value={paymentStatus}
                        onUpdate={(value: SelectValueType) => setPaymentStatus(String(value))}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} alignItems="center">
                    <Grid item xs={2.5}><Typography textAlign="left">Reason:</Typography></Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputProps={{ 'data-testid': 'reasonInput' }}
                        inputHeight={40}
                        sx={{ boxShadow: 'none' }}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  {/* A rejected-type status needs a rejection reason and date.
                      Both fields hide for every other status. */}
                  {isRejectedStatus && (
                    <>
                      <Grid container p={0.5} spacing={1} alignItems="center">
                        <Grid item xs={2.5}><Typography textAlign="left">Rejection reason</Typography></Grid>
                        <Grid item xs={8}>
                          <FHSelect
                            data-testid="rejectionReasonDropdown"
                            fullWidth
                            displayEmpty
                            inputHeight={40}
                            sx={{ boxShadow: 'none', borderRadius: '8px' }}
                            options={toSortedSelectOptions(Object.values(ClaimRejectionReasonV2))}
                            value={reasonForRejection}
                            onUpdate={(value: SelectValueType) => setReasonForRejection(String(value))}
                          />
                        </Grid>
                      </Grid>

                      <Grid container p={0.5} spacing={1} alignItems="center">
                        <Grid item xs={2.5}><Typography textAlign="left">Rejection date</Typography></Grid>
                        <Grid item xs={8}>
                          <FHAntdDateTimePicker
                            data-testid="rejectionDatePicker"
                            sx={{ width: '100%', boxShadow: 'none', borderRadius: '8px' }}
                            format="MM/DD/YYYY"
                            allowClear={false}
                            disabledDate={disabledFutureDate}
                            value={dateRejected ? moment(dateRejected) : ''}
                            onChange={(value: Moment) =>
                              setDateRejected(value ? moment(value).format('YYYY-MM-DD') : '')}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  <FHButton
                    data-testid="changeClaimStatusSubmitButton"
                    variant="contained"
                    sx={{ mt: 2, width: '200px' }}
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                  >
                    Submit
                  </FHButton>
                </Box>
              )}
            </Box>
          )}
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default ChangeClaimStatusForm;
